/* MOTURO cart guard · 0.1.0 (stock privacy, 01.10.2026)
   Same-origin AJAX for the real product form (/cart/add) and the real cart update (/cart/update). Stock stays on the
   server: Shopify (tracked inventory, policy deny) is the only enforcement; this file never limits or derives quantities.
   Shopify's error text is never read or shown (it can name stock numbers): every failure gets one generic German status
   and is followed by a read of the actual cart (/cart.js) or a fresh render of the cart page. Each request is sent at
   most once: no automatic retry, also not after a timeout or network error whose effect is uncertain. Checkout stays the
   native form post; while a cart update is pending it is blocked, afterwards the page re-renders with the server-side
   GPSR, staging, price and availability gates. Without JavaScript (or fetch) the native forms remain unchanged; clamped
   cart quantities stay observable, so this is no claim of absolute stock secrecy. */
(function (root) {
  'use strict';
  /* Locale strings: script#MoturoI18n (layout) holds the active theme locale, rendered with the native t filter and
     serialised with the json filter. The German literal is the base-locale (de.default) wording and is only used when
     no dictionary is on the page (offline tests); placeholders {{ name }} are filled as plain text. */
  const I18N = (() => { try { const el = typeof document !== 'undefined' && document.getElementById('MoturoI18n'); const d = el ? JSON.parse(el.textContent) : null; return d && typeof d === 'object' ? d : {}; } catch (_) { return {}; } })();
  const L = (name, base, vars) => { let s = typeof I18N[name] === 'string' ? I18N[name] : base; if (vars) for (const n of Object.keys(vars)) s = s.split('{{ ' + n + ' }}').join(String(vars[n])); return s; };
  const MESSAGES = {
    busy: L('cart_busy', 'Warenkorb wird aktualisiert …'),
    shortage: L('cart_add_shortage', 'Die gewünschte Menge ist derzeit nicht vollständig verfügbar. Bitte prüfe den Warenkorb – er zeigt den tatsächlichen Stand.'),
    uncertain: L('cart_add_uncertain', 'Der Warenkorb konnte gerade nicht bestätigt werden. Bitte prüfe den Warenkorb, bevor du es erneut versuchst.'),
    cart_shortage: L('cart_update_shortage', 'Nicht jede gewünschte Menge ist derzeit verfügbar. Der Warenkorb zeigt die tatsächlich übernommenen Mengen.'),
    cart_uncertain: L('cart_update_uncertain', 'Die Änderung konnte nicht bestätigt werden. Der Warenkorb zeigt den aktuellen Stand – bitte prüfe ihn.')
  };
  /* Cart tool accessible name after AJAX reconciliation: same wording as the Liquid header label
     (moturo.header.cart_label one/other, mirrored as cart_label_one/cart_label_other); plural form from the page
     language's CLDR rules, count only (never a stock figure). */
  function cartLabel(count, dict, lang) {
    const d = dict && typeof dict === 'object' ? dict : I18N;
    let form = count === 1 ? 'one' : 'other';
    try { if (lang && typeof Intl !== 'undefined' && Intl.PluralRules) form = new Intl.PluralRules(lang).select(count) === 'one' ? 'one' : 'other'; } catch (_) { /* keep simple rule */ }
    const s = typeof d['cart_label_' + form] === 'string' ? d['cart_label_' + form] : 'Warenkorb, {{ count }} Artikel';
    return s.split('{{ count }}').join(String(count));
  }
  const STATUS_PARAM = 'moturo_status';
  const TIMEOUT_MS = 15000;
  const HEADERS = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };

  /* Core without DOM (offline-tested): env = {fetch, navigate, setTimeout, clearTimeout, AbortController?, timeoutMs?}. */
  function create(env) {
    const timeoutMs = env.timeoutMs || TIMEOUT_MS;
    /* One bounded request. Outcome only: ok (+ JSON when asked), shortage (422) or uncertain. Error bodies stay unread. */
    async function send(url, init, wantJson) {
      const controller = env.AbortController ? new env.AbortController() : null;
      const timer = controller ? env.setTimeout(() => controller.abort(), timeoutMs) : null;
      try {
        const response = await env.fetch(url, Object.assign({ credentials: 'same-origin', signal: controller ? controller.signal : undefined }, init));
        if (response.status === 422) return { outcome: 'shortage' };
        if (!response.ok) return { outcome: 'uncertain' };
        if (!wantJson) return { outcome: 'ok' };
        try { return { outcome: 'ok', body: await response.json() }; } catch (_) { return { outcome: 'uncertain' }; }
      } catch (_) {
        return { outcome: 'uncertain' };
      } finally {
        if (timer !== null) env.clearTimeout(timer);
      }
    }
    const withStatus = (url, kind) => kind === 'shortage' || kind === 'uncertain' ? url + '?' + STATUS_PARAM + '=' + kind : url;

    /* Product form. view = {paths:{addJs, cart, cartJs}, isBusy(), busy(flag), status(kind), hasStatus(), formData(), count(n)}. */
    async function addToCart(view) {
      if (view.isBusy()) return 'ignored';                       // double click / second button: never a second add
      const body = view.formData();                              // before busy(): disabled controls are not submitted
      view.busy(true);
      view.status('busy');
      const result = await send(view.paths.addJs, { method: 'POST', headers: HEADERS, body }, false);
      if (result.outcome === 'ok') { env.navigate(view.paths.cart); return 'ok'; }   // the cart page renders the genuine state
      if (!view.hasStatus()) { env.navigate(withStatus(view.paths.cart, result.outcome)); return result.outcome; }
      const cart = await send(view.paths.cartJs, { method: 'GET', headers: HEADERS }, true);  // partial / clamped adds are possible
      if (cart.outcome === 'ok' && cart.body && Number.isInteger(cart.body.item_count)) view.count(cart.body.item_count);
      view.status(result.outcome);
      view.busy(false);
      return result.outcome;
    }

    /* updates[] is positional in cart line order, exactly like the native form post. Lines set to 0 disappear; every other
       line must come back with at least the requested quantity, else Shopify clamped or dropped it (shortage). A different
       line count than requested (e.g. a change from another tab) cannot be attributed and is reported as uncertain. */
    function compare(updates, cart) {
      if (!cart || !Array.isArray(cart.items)) return 'uncertain';
      const wanted = updates.filter(quantity => quantity > 0);
      if (cart.items.length > wanted.length) return 'uncertain';
      if (cart.items.length < wanted.length) return 'shortage';
      return wanted.every((quantity, i) => cart.items[i] && cart.items[i].quantity >= quantity) ? 'ok' : 'shortage';
    }

    /* Cart page. view = {paths:{updateJs, cart}, isBusy(), busy(flag), status(kind), updates()}. Always a fresh render after. */
    async function updateCart(view) {
      if (view.isBusy()) return 'ignored';
      const updates = view.updates();
      if (!updates) return 'native';
      view.busy(true);
      view.status('busy');
      const result = await send(view.paths.updateJs, { method: 'POST', headers: Object.assign({ 'Content-Type': 'application/json' }, HEADERS),
        body: JSON.stringify({ updates }) }, true);
      const kind = result.outcome === 'ok' ? compare(updates, result.body) : result.outcome;
      env.navigate(withStatus(view.paths.cart, kind));
      return kind;
    }
    return { addToCart, updateCart, compare };
  }

  /* ------------------------------------------------------------------ DOM binding */
  function bindDom(win) {
    const doc = win.document;
    const pageDict = (() => { try { const el = doc.getElementById('MoturoI18n'); const d = el ? JSON.parse(el.textContent) : null; return d && typeof d === 'object' ? d : I18N; } catch (_) { return I18N; } })();
    if (typeof win.fetch !== 'function' || typeof win.FormData !== 'function' || typeof win.URL !== 'function') return;  // native fallback
    const guard = create({ fetch: win.fetch.bind(win), AbortController: win.AbortController, setTimeout: win.setTimeout.bind(win),
      clearTimeout: win.clearTimeout.bind(win), navigate: url => win.location.assign(url) });
    const all = (selector, scope) => Array.prototype.slice.call((scope || doc).querySelectorAll(selector));
    const sameOrigin = form => {
      try { const url = new win.URL(form.getAttribute('action') || '', win.location.href); return url.origin === win.location.origin ? url : null; }
      catch (_) { return null; }
    };
    /* PDP: the status line in the product form. Cart page: one status line created under the page head (the cart section
       markup stays unchanged), also present when the fresh render shows an empty cart. */
    function cartStatus() {
      let element = doc.querySelector('[data-cart-status]');
      const head = doc.querySelector('.cart-page .page-head');
      if (!element && head) {
        element = doc.createElement('p');
        element.className = 'meta'; element.hidden = true;
        element.setAttribute('data-cart-status', ''); element.setAttribute('role', 'status'); element.setAttribute('aria-live', 'polite');
        head.insertAdjacentElement('afterend', element);
      }
      return element;
    }
    const statusElement = form => form.classList.contains('cart-layout') ? cartStatus() : form.querySelector('[data-cart-status]');
    function show(element, kind) {
      if (!element) return;
      if (!kind || !MESSAGES[kind]) { element.textContent = ''; element.hidden = true; return; }
      element.hidden = false;
      element.textContent = MESSAGES[kind];
    }
    function setCount(count) {
      all('a.tool').forEach(link => {
        let path = '';
        try { path = new win.URL(link.href, win.location.href).pathname; } catch (_) { return; }
        if (!/\/cart$/.test(path)) return;
        link.setAttribute('aria-label', cartLabel(count, pageDict, doc.documentElement && doc.documentElement.lang));
        let badge = link.querySelector('.tool-badge');
        if (count > 0 && !badge) { badge = doc.createElement('span'); badge.className = 'tool-badge'; badge.setAttribute('aria-hidden', 'true'); link.appendChild(badge); }
        if (badge) { if (count > 0) badge.textContent = String(count); else badge.remove(); }
      });
    }
    function view(form, paths) {
      const saved = new Map();
      const buttons = () => all('button[type="submit"],input[type="submit"]', form).concat(form.id ? all('[type="submit"]').filter(b => b.getAttribute('form') === form.id) : []);
      const inputs = () => all('input[name="quantity"],input[name="updates[]"],select[name="id"]', form);
      return {
        paths,
        isBusy: () => form.dataset.cartBusy === 'true',
        busy(flag) {
          if (flag) {
            form.dataset.cartBusy = 'true'; form.setAttribute('aria-busy', 'true');
            buttons().forEach(b => { saved.set(b, b.disabled); b.disabled = true; });
            inputs().forEach(i => { saved.set(i, i.disabled); i.disabled = true; });
          } else {
            delete form.dataset.cartBusy; form.removeAttribute('aria-busy');
            saved.forEach((disabled, element) => { element.disabled = disabled; });
            saved.clear();
          }
        },
        status: kind => show(statusElement(form), kind),
        hasStatus: () => !!statusElement(form),
        formData: () => new win.FormData(form),
        count: setCount,
        updates() {
          const values = all('input[name="updates[]"]', form).map(input => String(input.value).trim());
          if (!values.length || !values.every(value => /^[0-9]{1,6}$/.test(value))) return null;   // unexpected -> native post
          return values.map(Number);
        }
      };
    }
    doc.addEventListener('submit', event => {
      const form = event.target;
      if (event.defaultPrevented || !form || form.tagName !== 'FORM') return;
      const url = sameOrigin(form);
      if (!url) return;
      if (form.classList.contains('cart-layout')) {
        if (form.dataset.cartBusy === 'true') { event.preventDefault(); return; }   // incl. checkout while an update is pending
        const submitter = event.submitter;
        if (!submitter || submitter.name !== 'update') return;                     // checkout and everything else: native
        const v = view(form, { updateJs: url.pathname.replace(/\/$/, '') + '/update.js', cart: url.pathname });
        if (!v.updates()) return;
        event.preventDefault();
        guard.updateCart(v);
        return;
      }
      if (!/\/cart\/add$/.test(url.pathname)) return;
      event.preventDefault();
      if (form.dataset.cartBusy === 'true') return;
      const base = url.pathname.replace(/\/add$/, '');
      guard.addToCart(view(form, { addJs: url.pathname + '.js', cart: base, cartJs: base + '.js' }));
    });
    /* Generic status carried to the fresh cart render; only whitelisted kinds, the parameter is removed again. */
    try {
      const params = new win.URLSearchParams(win.location.search), kind = params.get(STATUS_PARAM);
      if (kind) {
        if (kind === 'shortage' || kind === 'uncertain') { const element = cartStatus(); if (element) win.setTimeout(() => show(element, 'cart_' + kind), 50); }
        params.delete(STATUS_PARAM);
        const query = params.toString();
        win.history.replaceState(null, '', win.location.pathname + (query ? '?' + query : '') + win.location.hash);
      }
    } catch (_) {}
  }

  root.MoturoCartGuard = { create, MESSAGES, STATUS_PARAM, cartLabel };
  if (root.document && root.document.addEventListener) bindDom(root);
})(typeof window !== 'undefined' ? window : globalThis);
